// Read-only mode (W14 gate, W12 step A5). Quil's own file, not canonical; the same shape as bindr's,
// inkling's and contently's.
//
// Two things live here.
//
// 1. W14's stale-build gate (`BuildGate`, `decideBoot`) takes a `newerData` input: the local data was
//    written by a NEWER build, so this one must never write over it. Until W12 nothing could raise
//    it. Now `quilScope.openStore` raises it when the local store's version is ahead of this build
//    (`NewerDataError`) and calls `noteNewerData()`, which feeds the gate here. "Newer data seen" is
//    recorded against the build that saw it, in localStorage, so it survives the reload that follows
//    and clears itself the moment a different (newer) build boots: that build can read the data.
//
// 2. What THIS session's store is open as, in memory (`setReadOnly` / `useReadOnly`), so the sync
//    pill can say so. The session that meets a newer, or an un-upgradable, store opens it through a
//    view that refuses every write (quilScope.openReadOnlyView) and never talks to OneDrive.
import { useSyncExternalStore } from 'react'
import { NEWER_DATA_KEY } from './scopeKeys'

declare const __BUILD_INFO__: { buildId: string } | undefined

function runningBuild(): string {
  try {
    return __BUILD_INFO__?.buildId ?? 'unknown'
  } catch {
    return 'unknown'
  }
}

/** A newer build's data was found while opening the local store. Recorded for the next boot's gate. */
export function noteNewerData(buildId: string = runningBuild()): void {
  try {
    localStorage.setItem(NEWER_DATA_KEY, buildId)
  } catch {
    /* storage blocked: the store was opened read-only this session regardless */
  }
}

/** Has THIS build already met newer data (in an earlier boot)? Fails closed to false. */
export function newerDataSeen(buildId: string = runningBuild()): boolean {
  try {
    const seen = localStorage.getItem(NEWER_DATA_KEY)
    if (seen && seen !== buildId) localStorage.removeItem(NEWER_DATA_KEY)
    return seen === buildId
  } catch {
    return false
  }
}

/** Tests, and nothing else.
 * @internal Test seam: resets module state between tests. Never imported by the app.
 */
export function clearNewerData(): void {
  try {
    localStorage.removeItem(NEWER_DATA_KEY)
  } catch {
    /* ignore */
  }
}

// ------------------------------------------------------------------------------ this session

export interface ReadOnlyInfo {
  /** `newer`: saved by a newer build. `held`: no safe backup, so not upgraded. `failed`: the upgrade did not take. */
  kind: 'newer' | 'held' | 'failed'
  reason: string
}

let current: ReadOnlyInfo | null = null
const listeners = new Set<() => void>()

/** Record what this tab's store is open as (null when it is writable). */
export function setReadOnly(info: ReadOnlyInfo | null): void {
  current = info
  for (const l of [...listeners]) l()
}

export function getReadOnly(): ReadOnlyInfo | null {
  return current
}

function subscribe(l: () => void): () => void {
  listeners.add(l)
  return () => { listeners.delete(l) }
}

/** Is this session's store read-only? The sync pill reads it. */
export function useReadOnly(): ReadOnlyInfo | null {
  return useSyncExternalStore(subscribe, getReadOnly, getReadOnly)
}
